/**
 * Reach → Leads, the screen itself — slice 70. `ReachLeadsPage` fetches and
 * acts; this draws, so `verify-sense-screens` can render it outside Vite (the
 * page reaches `lib/config.ts`, which only evaluates inside Vite).
 *
 * ## WHAT THE PAGE IS FOR
 *
 * Reach buys attention on the workspace's own Meta ad account; until slice 70
 * the people that attention produced vanished into a WhatsApp inbox. Now a
 * click-to-WhatsApp lead lands in Core as a person (the system "Reach leads"
 * dataset), and this page is where Reach shows them: how many this week, which
 * ad brought them, the newest few — and the one thing to do next, which is to
 * greet them with a flow. That is the loop the owner asked to see: Reach finds
 * a person, Flow talks to them through Connect, Sense reads what happened.
 *
 * ## MASKED, ALWAYS
 *
 * The contract carries a lead as a name and one masked address (`a***@domain`
 * or `+91 ••••• ••123`), and this file prints exactly that. There is no
 * "reveal": a person who needs the full address opens the person in their own
 * audience, where the same rules as every upload apply.
 *
 * ## ZERO, ABSENT, AND "NOTHING HAS EVER ARRIVED"
 *
 * `hasLeadsDataset: false` means no lead has ever reached this workspace: the
 * page explains how leads arrive (a click-to-WhatsApp ad, or Meta's lead
 * export imported here) instead of drawing a 0 that would read as "your ads
 * brought nobody". Once a lead has arrived, a week with none is a measured 0
 * and is drawn as one.
 *
 * ## ONE PRIMARY
 *
 * "Start a welcome flow" is the page's one primary action, and it opens the
 * flow builder on the welcome template — a DRAFT. Nothing here launches or
 * sends; the furthest this page goes is a form.
 */
import {
  FLOW_TEMPLATES,
  REACH_LEAD_CHANNEL_LABEL,
  type ReachLead,
  type ReachLeadsByAd,
  type ReachLeadsResponse,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Kicker,
  MetricPanel,
  PersonList,
  PersonRow,
  Tag,
  buttonVariants,
  cn,
  focusRing,
} from '@wend/ui';
import { Link } from 'react-router-dom';

import { formatCount, formatDate, formatDateTime } from '../../../lib/format';

/** The welcome template's own address in the flow builder. */
export const WELCOME_FLOW_HREF = '/flows/new?template=welcome_leads';

export interface ReachLeadsHandlers {
  /** Narrow the list to one ad, or `null` for every ad. */
  readonly onPickAd: (adId: string | null) => void;
  readonly onPage: (page: number) => void;
  /** Opens the file picker for Meta's lead export. */
  readonly onImport: () => void;
  /** True while an import is in flight. */
  readonly importing: boolean;
}

/** How an ad is named on screen: its headline, else its id, else the absence said in words. */
export function adLabel(row: { readonly adId: string | null; readonly adHeadline: string | null }): string {
  if (row.adHeadline !== null && row.adHeadline.trim() !== '') return row.adHeadline;
  if (row.adId !== null) return `Ad ${row.adId}`;
  return 'No ad recorded';
}

export function ReachLeadsContent({
  leads,
  adFilter,
  handlers,
}: {
  leads: ReachLeadsResponse;
  adFilter: string | null;
  handlers: ReachLeadsHandlers;
}) {
  if (!leads.hasLeadsDataset) {
    return <NoLeadsYet handlers={handlers} />;
  }

  const filtered = adFilter === null ? null : leads.byAd.find((row) => row.adId === adFilter) ?? null;

  return (
    <div className="flex flex-col gap-6" data-role="reach-leads">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <MetricPanel
          kicker="Leads this week"
          kickerAs="h2"
          figure={{ kind: 'measured', value: leads.totals.lastSevenDays }}
          sub={`${formatCount(leads.totals.leads)} in all, from every ad`}
          live="polite"
          data-role="reach-leads-week"
        />
        <Callout tone="brand" title="Greet them while they are warm">
          <div className="flex flex-col items-start gap-3">
            <p className="text-[0.88rem] leading-relaxed text-muted">
              {FLOW_TEMPLATES.welcome_leads.description} It opens as a draft: you check it and launch it yourself.
            </p>
            <Link to={WELCOME_FLOW_HREF} className={buttonVariants({ size: 'sm' })} data-role="reach-leads-welcome">
              Start a welcome flow
            </Link>
          </div>
        </Callout>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By ad</CardTitle>
        </CardHeader>
        <CardContent>
          {leads.byAd.length === 0 ? (
            <p className="text-[0.88rem] text-muted">No lead has an ad recorded yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line" data-role="reach-leads-by-ad">
              {leads.byAd.map((row) => (
                <ByAdRow key={row.adId ?? 'none'} row={row} picked={row.adId !== null && row.adId === adFilter} onPickAd={handlers.onPickAd} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle>{filtered === null ? 'Recent leads' : `Leads from ${adLabel(filtered)}`}</CardTitle>
          <span className="flex items-center gap-2">
            {adFilter !== null && (
              <Button size="sm" variant="ghost" onClick={() => handlers.onPickAd(null)}>
                Every ad
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={handlers.onImport} loading={handlers.importing}>
              Import Meta’s lead export
            </Button>
          </span>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {leads.items.length === 0 ? (
            <p className="text-[0.88rem] text-muted" data-role="reach-leads-none">
              {adFilter === null ? 'No leads on this page.' : 'No leads from this ad.'}
            </p>
          ) : (
            <PersonList aria-label="Recent leads" data-role="reach-leads-list">
              {leads.items.map((lead) => (
                <LeadRow key={lead.contactId} lead={lead} />
              ))}
            </PersonList>
          )}
          <p className="text-[0.78rem] text-muted">
            Addresses are masked here. A lead is an ordinary person in your audience, under the “Reach leads” list.
          </p>
          {leads.pagination.totalPages > 1 && (
            <nav aria-label="Pages of leads" className="flex items-center justify-between gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={leads.pagination.page <= 1}
                onClick={() => handlers.onPage(leads.pagination.page - 1)}
              >
                Newer
              </Button>
              <span className="text-[0.8rem] text-muted" aria-live="polite">
                Page {formatCount(leads.pagination.page)} of {formatCount(leads.pagination.totalPages)}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={leads.pagination.page >= leads.pagination.totalPages}
                onClick={() => handlers.onPage(leads.pagination.page + 1)}
              >
                Older
              </Button>
            </nav>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ByAdRow({
  row,
  picked,
  onPickAd,
}: {
  row: ReachLeadsByAd;
  picked: boolean;
  onPickAd: (adId: string | null) => void;
}) {
  const label = adLabel(row);
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0" data-role="reach-leads-ad">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[0.9rem] font-medium text-text">{label}</span>
        <Kicker as="span">
          {row.latestAt === null ? 'no lead dated yet' : `latest ${formatDate(row.latestAt)}`}
        </Kicker>
      </div>
      <div className="flex items-center gap-4">
        <span className="flex flex-col items-end">
          <span className="text-[1.05rem] font-semibold tabular-nums text-text">{formatCount(row.leads)}</span>
          <span className="text-[0.72rem] text-muted">{formatCount(row.lastSevenDays)} this week</span>
        </span>
        {row.adId !== null && (
          <button
            type="button"
            aria-pressed={picked}
            aria-label={`Show only the leads from ${label}`}
            onClick={() => onPickAd(picked ? null : row.adId)}
            className={cn(
              'rounded-sm border px-2 py-1 text-[0.78rem]',
              picked ? 'border-brand-b/50 bg-brand-a/18 text-text' : 'border-line text-muted hover:text-text',
              focusRing,
            )}
          >
            {picked ? 'Showing' : 'Show'}
          </button>
        )}
      </div>
    </li>
  );
}

function LeadRow({ lead }: { lead: ReachLead }) {
  const name = lead.person.name ?? 'Name not given';
  const meta = [REACH_LEAD_CHANNEL_LABEL[lead.channel], lead.adId === null && lead.adHeadline === null ? null : adLabel(lead), formatDateTime(lead.createdAt)]
    .filter((part): part is string => part !== null)
    .join(' · ');
  return (
    <PersonRow
      name={name}
      seed={lead.person.id}
      meta={meta}
      contact={lead.person.address ?? 'no address on file'}
      tag={lead.inActiveFlow ? <Tag tone="brand">in a flow</Tag> : undefined}
      data-role="reach-lead"
    />
  );
}

/** No lead has ever arrived: how they do, instead of a 0. */
function NoLeadsYet({ handlers }: { handlers: ReachLeadsHandlers }) {
  return (
    <Card data-role="reach-leads-empty">
      <CardContent className="flex flex-col items-start gap-3 p-[18px]">
        <p className="text-[0.95rem] font-medium text-text">No leads have arrived yet.</p>
        <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
          A lead lands here when somebody taps one of your click-to-WhatsApp ads and writes to your number, or when you
          import the lead export Meta gives you for a lead form. Each one becomes a person in your audience, with the
          ad that brought them, and can start a flow.
        </p>
        <span className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={handlers.onImport} loading={handlers.importing}>
            Import Meta’s lead export
          </Button>
          <Link to="/reach/ads" className={cn('text-[0.85rem] text-text underline underline-offset-4', focusRing)}>
            See your ads
          </Link>
        </span>
      </CardContent>
    </Card>
  );
}
